import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Zap, ArrowRight, ArrowLeft, CheckCircle, Sparkles, 
  Download, Loader2, Send, User, Bot, ChevronRight,
  Target, Heart, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Questions de contexte
const CONTEXT_QUESTIONS = [
  { id: 1, question: "Décris ton activité en quelques mots", placeholder: "Ex: Coach en développement personnel, formatrice en ligne..." },
  { id: 2, question: "Quelle est ta cible ?", placeholder: "BtoB/BtoC, profil : salariés, indépendants, entreprises..." },
  { id: 3, question: "Pourquoi tes clients viennent-ils te voir ?", placeholder: "Qu'achètent-ils ? Quel problème cherchent-ils à résoudre ?" },
  { id: 4, question: "Comment étaient-ils AVANT et quelle transformation ont-ils vécue APRÈS ?", placeholder: "Décris le changement observé chez tes clients..." },
  { id: 5, question: "Quel est le problème majeur de tes clients ?", placeholder: "Le problème principal dont ils sont conscients (max 3)" },
  { id: 6, question: "As-tu des retours clients ?", placeholder: "Partage des témoignages ou feedbacks si tu en as..." },
  { id: 7, question: "Sur quels réseaux sociaux publies-tu ?", placeholder: "Instagram, LinkedIn, Facebook, TikTok..." },
  { id: 8, question: "Explique-moi ton offre actuelle", placeholder: "Détaille tes produits/services, tarifs, format..." }
];

const STEP_INFO = {
  1: { title: "Les 10 douleurs principales", icon: AlertTriangle, color: "text-red-500" },
  2: { title: "Les 10 besoins principaux", icon: Heart, color: "text-blue-500" },
  3: { title: "Les 10 frustrations principales", icon: Target, color: "text-orange-500" }
};

export default function AvatarTrial() {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const resultRef = useRef(null);
  
  // États
  const [phase, setPhase] = useState('form'); // form, context, analyzing, results
  const [sessionId, setSessionId] = useState(urlSessionId || null);
  const [userName, setUserName] = useState('');
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Questions de contexte
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [contextResponses, setContextResponses] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  
  // Étapes d'analyse
  const [currentStep, setCurrentStep] = useState(0);
  const [stepResults, setStepResults] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [stepError, setStepError] = useState(null); // Pour gérer les erreurs par étape
  
  // Charger une session existante si URL fournie
  useEffect(() => {
    if (urlSessionId) {
      loadSession(urlSessionId);
    }
  }, [urlSessionId]);

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API}/api/avatar-trial/session/${sid}`);
      const session = response.data;
      
      setSessionId(sid);
      setUserName(session.first_name);
      
      if (session.completed) {
        setPhase('results');
        setCurrentStep(3);
        // Charger les résultats existants
        for (let i = 1; i <= 3; i++) {
          if (session.completed_steps.includes(String(i))) {
            const stepResponse = await axios.post(`${API}/api/avatar-trial/analyze`, {
              session_id: sid,
              step_number: i
            });
            setStepResults(prev => ({ ...prev, [i]: stepResponse.data.content }));
          }
        }
      } else if (session.has_context) {
        setPhase('analyzing');
        setCurrentStep(session.current_step || 1);
      } else {
        setPhase('context');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Session non trouvée');
      navigate('/essai-gratuit');
    }
  };

  // Inscription
  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.email.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (!acceptedTerms) {
      toast.error('Veuillez accepter les conditions d\'utilisation');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/avatar-trial/start`, {
        first_name: formData.firstName.trim(),
        email: formData.email.trim().toLowerCase()
      });

      setSessionId(response.data.session_id);
      setUserName(response.data.user_name);
      toast.success(`Bienvenue ${response.data.user_name} ! Commençons.`);
      setPhase('context');
    } catch (error) {
      console.error('Signup error:', error);
      if (error.response?.status === 409) {
        toast.error('Un compte existe déjà avec cet email. Connectez-vous pour continuer.');
        navigate('/auth', { state: { email: formData.email } });
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  // Soumettre une réponse de contexte
  const handleContextAnswer = () => {
    if (!currentAnswer.trim()) {
      toast.error('Veuillez répondre à la question');
      return;
    }

    const question = CONTEXT_QUESTIONS[currentQuestionIndex];
    setContextResponses(prev => ({
      ...prev,
      [question.id]: { question: question.question, answer: currentAnswer.trim() }
    }));
    setCurrentAnswer('');

    if (currentQuestionIndex < CONTEXT_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      submitContext();
    }
  };

  // Soumettre tout le contexte
  const submitContext = async () => {
    setLoading(true);
    try {
      const responsesArray = CONTEXT_QUESTIONS.map(q => ({
        question: q.question,
        answer: contextResponses[q.id]?.answer || currentAnswer.trim()
      }));

      await axios.post(`${API}/api/avatar-trial/submit-context`, {
        session_id: sessionId,
        responses: responsesArray
      });

      toast.success('Contexte enregistré ! Démarrons l\'analyse.');
      setPhase('analyzing');
      setCurrentStep(1);
      
      // Démarrer automatiquement l'étape 1
      analyzeStepWithStreaming(1);
    } catch (error) {
      console.error('Context submit error:', error);
      toast.error('Erreur lors de l\'enregistrement du contexte');
    } finally {
      setLoading(false);
    }
  };

  // Analyser une étape avec streaming en temps réel
  const analyzeStepWithStreaming = async (stepNumber) => {
    setAnalyzing(true);
    setStreamingContent('');
    setCurrentStep(stepNumber);
    setStepError(null); // Reset l'erreur
    
    try {
      const response = await fetch(`${API}/api/avatar-trial/analyze-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          step_number: stepNumber
        })
      });

      if (!response.ok) {
        throw new Error('Streaming non disponible');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let hasError = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                hasError = true;
                throw new Error(data.error);
              }
              
              if (data.chunk) {
                fullContent += data.chunk;
                setStreamingContent(fullContent);
              }
              
              if (data.done) {
                setStepResults(prev => ({ ...prev, [stepNumber]: fullContent }));
                setStreamingContent('');
                
                if (data.is_final_step) {
                  // NE PAS passer directement aux résultats - laisser l'utilisateur voir l'étape 3
                  // Envoyer l'email automatiquement en arrière-plan
                  try {
                    await axios.post(`${API}/api/avatar-trial/send-email/${sessionId}`);
                    toast.success('Votre analyse complète a été envoyée par email !');
                  } catch (emailError) {
                    console.error('Email error:', emailError);
                  }
                }
              }
            } catch (parseError) {
              if (parseError.message && parseError.message !== 'Unexpected end of JSON input') {
                hasError = true;
                throw parseError;
              }
            }
          }
        }
      }
      
      // Si on a du contenu mais pas de signal "done", on considère que c'est terminé
      if (fullContent && !stepResults[stepNumber]) {
        setStepResults(prev => ({ ...prev, [stepNumber]: fullContent }));
        setStreamingContent('');
      }
      
      // Scroll vers le résultat
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
    } catch (error) {
      console.error('Streaming error, falling back to non-streaming:', error);
      // Fallback vers l'endpoint non-streaming
      try {
        await analyzeStepNonStreaming(stepNumber);
      } catch (fallbackError) {
        // Si même le fallback échoue, on affiche l'erreur
        setStepError(stepNumber);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyser une étape sans streaming (fallback)
  const analyzeStepNonStreaming = async (stepNumber) => {
    setStepError(null);
    try {
      const response = await axios.post(`${API}/api/avatar-trial/analyze`, {
        session_id: sessionId,
        step_number: stepNumber
      }, { timeout: 200000 }); // 3+ minutes timeout

      setStepResults(prev => ({ ...prev, [stepNumber]: response.data.content }));
      setStreamingContent('');
      
      if (response.data.is_final_step) {
        // NE PAS passer directement aux résultats - laisser l'utilisateur voir l'étape 3
        // Envoyer l'email automatiquement en arrière-plan
        try {
          await axios.post(`${API}/api/avatar-trial/send-email/${sessionId}`);
          toast.success('Votre analyse complète a été envoyée par email !');
        } catch (emailError) {
          console.error('Email error:', emailError);
        }
      }
      
      // Scroll vers le résultat
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
    } catch (error) {
      console.error('Analyze error:', error);
      toast.error('Erreur lors de l\'analyse. Veuillez réessayer.');
      setStepError(stepNumber);
      throw error;
    }
  };

  // Analyser une étape (essaie streaming, fallback non-streaming)
  const analyzeStep = async (stepNumber) => {
    return analyzeStepWithStreaming(stepNumber);
  };

  // Navigation entre étapes
  const goToNextStep = () => {
    if (currentStep < 3) {
      analyzeStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Télécharger le PDF
  const downloadPDF = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/avatar-trial/download-pdf/${sessionId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Avatar_Analyse_${userName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setLoading(false);
    }
  };

  // Aller vers les offres
  const goToOffers = () => {
    navigate('/pricing');
  };

  // Rendu du formulaire d'inscription
  const renderSignupForm = () => (
    <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
      {/* Left - Benefits */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-6"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Créez votre <span className="text-primary">Avatar Client</span>
          </h1>
          <p className="text-lg text-muted">
            Découvrez gratuitement les 3 premières étapes de votre analyse marketing.
          </p>
        </div>

        <div className="space-y-4">
          {[
            'Les 10 douleurs principales de votre client idéal',
            'Les 10 besoins profonds qui le motivent',
            'Les 10 frustrations qui le bloquent',
            'Analyse personnalisée par IA',
            'PDF téléchargeable de vos résultats'
          ].map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="flex items-center gap-3"
            >
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
              <span>{benefit}</span>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center gap-2 text-sm text-muted">
          <Sparkles className="w-4 h-4 text-primary" />
          <span>Sans engagement • Sans carte bancaire</span>
        </div>
      </motion.div>

      {/* Right - Form */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-heading font-bold mb-2">Commencez maintenant</h2>
            <p className="text-sm text-muted">Accédez à votre essai gratuit en 30 secondes</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Prénom</label>
              <Input
                type="text"
                placeholder="Votre prénom"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={loading}
                required
                data-testid="firstname-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                placeholder="votre@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={loading}
                required
                data-testid="email-input"
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-xs text-muted">
                J'accepte les{' '}
                <a href="/terms" target="_blank" className="text-primary hover:underline">
                  conditions d'utilisation
                </a>{' '}
                et la{' '}
                <a href="/privacy" target="_blank" className="text-primary hover:underline">
                  politique de confidentialité
                </a>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full shadow-glow-md"
              disabled={loading}
              data-testid="start-trial-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  Démarrer mon essai gratuit
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-muted mt-4">
            Déjà un compte ?{' '}
            <a href="/auth" className="text-primary hover:underline">
              Connectez-vous
            </a>
          </p>
        </Card>
      </motion.div>
    </div>
  );

  // Rendu des questions de contexte
  const renderContextQuestions = () => {
    const question = CONTEXT_QUESTIONS[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / CONTEXT_QUESTIONS.length) * 100;

    return (
      <div className="w-full max-w-2xl mx-auto">
        <motion.div
          key={currentQuestionIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="space-y-6"
        >
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted">
              <span>Question {currentQuestionIndex + 1} / {CONTEXT_QUESTIONS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <Card className="p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg mb-1">
                  {question.question}
                </h3>
                <p className="text-sm text-muted">
                  Plus tu donnes de détails, plus l'analyse sera pertinente.
                </p>
              </div>
            </div>

            <Textarea
              placeholder={question.placeholder}
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              rows={5}
              className="mb-4"
              disabled={loading}
              data-testid="context-answer-input"
            />

            <div className="flex gap-3">
              {currentQuestionIndex > 0 && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentQuestionIndex(prev => prev - 1);
                    const prevQuestion = CONTEXT_QUESTIONS[currentQuestionIndex - 1];
                    setCurrentAnswer(contextResponses[prevQuestion.id]?.answer || '');
                  }}
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
              )}
              
              <Button
                onClick={handleContextAnswer}
                disabled={loading || !currentAnswer.trim()}
                className="flex-1 shadow-glow-sm"
                data-testid="next-question-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : currentQuestionIndex === CONTEXT_QUESTIONS.length - 1 ? (
                  <>
                    Lancer l'analyse
                    <Sparkles className="ml-2 w-4 h-4" />
                  </>
                ) : (
                  <>
                    Continuer
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Tip */}
          <div className="flex items-center gap-2 text-sm text-muted justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>Astuce : N'hésite pas à donner des exemples concrets</span>
          </div>
        </motion.div>
      </div>
    );
  };

  // Rendu de l'analyse
  const renderAnalysis = () => {
    const StepIcon = STEP_INFO[currentStep]?.icon || Target;
    
    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header avec progression */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-heading font-bold">
              Analyse de ton Avatar
            </h2>
            <div className="text-sm text-muted">
              Étape {currentStep} / 3
            </div>
          </div>
          
          {/* Steps indicator */}
          <div className="flex gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  step < currentStep ? 'bg-primary' :
                  step === currentStep ? 'bg-primary/60' :
                  'bg-muted/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step buttons */}
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((step) => {
            const info = STEP_INFO[step];
            const Icon = info.icon;
            const isCompleted = stepResults[step];
            const isCurrent = step === currentStep;
            
            return (
              <button
                key={step}
                onClick={() => isCompleted && setCurrentStep(step)}
                disabled={!isCompleted && step !== currentStep}
                className={`p-4 rounded-xl border transition-all text-left ${
                  isCurrent ? 'border-primary bg-primary/10' :
                  isCompleted ? 'border-primary/50 bg-card hover:bg-primary/5 cursor-pointer' :
                  'border-muted/30 bg-muted/10 opacity-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${info.color}`} />
                  {isCompleted && <CheckCircle className="w-4 h-4 text-green-500" />}
                </div>
                <div className="text-sm font-medium">Étape {step}</div>
                <div className="text-xs text-muted truncate">{info.title}</div>
              </button>
            );
          })}
        </div>

        {/* Current step content */}
        <Card className="p-6 overflow-x-auto" ref={resultRef}>
          {analyzing && streamingContent ? (
            // Affichage du streaming en temps réel
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-primary mb-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Génération en cours...</span>
              </div>
              <div className="prose prose-sm max-w-none dark:prose-invert prose-table:w-full prose-th:bg-primary/10 prose-th:p-2 prose-th:text-left prose-td:p-2 prose-td:border prose-td:border-border/50">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
                <span className="inline-block w-2 h-5 bg-primary animate-pulse ml-1" />
              </div>
            </div>
          ) : analyzing ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <div className="text-center">
                <h3 className="font-heading font-bold mb-2">
                  Analyse en cours...
                </h3>
                <p className="text-sm text-muted">
                  {STEP_INFO[currentStep]?.title}
                </p>
              </div>
            </div>
          ) : stepResults[currentStep] ? (
            <div className="prose prose-sm max-w-none dark:prose-invert prose-table:w-full prose-th:bg-primary/10 prose-th:p-2 prose-th:text-left prose-td:p-2 prose-td:border prose-td:border-border/50">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{stepResults[currentStep]}</ReactMarkdown>
            </div>
          ) : stepError === currentStep ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <div className="text-center">
                <h3 className="font-heading font-bold mb-2 text-red-400">
                  Erreur lors de l'analyse
                </h3>
                <p className="text-sm text-muted mb-4">
                  Une erreur s'est produite. Cliquez ci-dessous pour réessayer.
                </p>
                <Button
                  onClick={() => analyzeStep(currentStep)}
                  className="bg-red-500 hover:bg-red-600"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Réessayer l'analyse
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted">
              Cliquez sur "Analyser" pour commencer cette étape
            </div>
          )}
        </Card>

        {/* Navigation buttons */}
        <div className="flex gap-4 justify-between">
          <Button
            variant="outline"
            onClick={goToPreviousStep}
            disabled={currentStep <= 1 || analyzing}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Étape précédente
          </Button>
          
          {currentStep < 3 ? (
            <Button
              onClick={goToNextStep}
              disabled={!stepResults[currentStep] || analyzing}
              className="shadow-glow-sm"
              data-testid="next-step-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  Étape suivante
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          ) : stepResults[3] ? (
            <Button
              onClick={() => setPhase('results')}
              className="shadow-glow-sm bg-green-600 hover:bg-green-700"
            >
              Voir mes résultats
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={() => analyzeStep(3)}
              disabled={analyzing}
              className="shadow-glow-sm"
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  Analyser l'étape 3
                  <Sparkles className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Rendu des résultats finaux
  const renderResults = () => (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-3xl font-heading font-bold">
          Bravo {userName} !
        </h1>
        <p className="text-lg text-muted">
          Tu as complété les 3 premières étapes de ton analyse d'avatar.
        </p>
      </motion.div>

      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {[1, 2, 3].map((step) => {
          const info = STEP_INFO[step];
          const Icon = info.icon;
          return (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: step * 0.1 }}
            >
              <Card 
                className="p-4 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setPhase('analyzing');
                  setCurrentStep(step);
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className={`w-5 h-5 ${info.color}`} />
                  <span className="font-medium">Étape {step}</span>
                </div>
                <p className="text-sm text-muted">{info.title}</p>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-6"
      >
        {/* CTA vers offres - PRIORITAIRE */}
        <Card className="p-8 bg-gradient-to-br from-pink-500/20 via-rose-500/15 to-primary/10 border-2 border-pink-500/40 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-6 h-6 text-pink-400" />
              <h3 className="font-heading font-bold text-xl text-white">
                Envie d'aller plus loin ?
              </h3>
            </div>
            
            <p className="text-base text-gray-200 mb-6 leading-relaxed">
              Tu viens de découvrir <strong className="text-pink-300">3 étapes</strong> de ton avatar. 
              L'analyse complète en comprend <strong className="text-pink-300">47</strong> : avatar ultra-détaillé, 
              analyse concurrentielle, marketing disruptif et ta <strong className="text-pink-300">Big Idea</strong>.
            </p>
            
            <Button
              onClick={goToOffers}
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold shadow-lg shadow-pink-500/25 hover:shadow-pink-500/40 transition-all"
              data-testid="view-offers-btn"
            >
              Découvrir les offres Pro & Agence
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </Card>

        {/* PDF download - bouton */}
        <div className="flex justify-center">
          <Button
            onClick={downloadPDF}
            disabled={loading}
            variant="outline"
            className="border-primary/50 hover:bg-primary/10"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {loading ? 'Génération...' : 'Télécharger mes résultats'}
          </Button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 sm:p-6 border-b border-border/50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg sm:text-xl">Boost-StrategIA</span>
          </a>
          
          {phase !== 'form' && userName && (
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-muted" />
              <span>{userName}</span>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <AnimatePresence mode="wait">
          {phase === 'form' && renderSignupForm()}
          {phase === 'context' && renderContextQuestions()}
          {phase === 'analyzing' && renderAnalysis()}
          {phase === 'results' && renderResults()}
        </AnimatePresence>
      </main>
    </div>
  );
}
